/**
 * SourceBasket — Compact summary list of accumulated file and connector sources.
 */
import React from 'react';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemSecondaryAction,
  ListItemText,
  Paper,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  AttachFile,
  Cable,
  DeleteOutline,
} from '@mui/icons-material';
import type { ConnectorSourceEntry } from './ConnectorSourcePicker';
import { formatFileSize, buildFileSignature } from './FileDropZone';

interface SourceBasketProps {
  files: File[];
  connectorSources: ConnectorSourceEntry[];
  onRemoveFile: (signature: string) => void;
  onRemoveConnectorSource: (key: string) => void;
}

const SourceBasket: React.FC<SourceBasketProps> = ({
  files,
  connectorSources,
  onRemoveFile,
  onRemoveConnectorSource,
}) => {
  const totalCount = files.length + connectorSources.length;
  if (totalCount === 0) return null;

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" color="text.secondary">
          Selected Sources
        </Typography>
        <Chip label={totalCount} size="small" color="primary" sx={{ height: 20, fontSize: '0.75rem' }} />
      </Stack>
      <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto' }}>
        <List dense disablePadding>
          {files.map((file, index) => {
            const sig = buildFileSignature(file);
            return (
              <React.Fragment key={`file-${sig}`}>
                <ListItem sx={{ pr: 6 }}>
                  <ListItemAvatar sx={{ minWidth: 36 }}>
                    <Avatar sx={{ bgcolor: 'primary.light', color: 'primary.dark', width: 24, height: 24 }}>
                      <AttachFile sx={{ fontSize: 14 }} />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={file.name}
                    secondary={formatFileSize(file.size)}
                    primaryTypographyProps={{ variant: 'body2', fontSize: 12, noWrap: true }}
                    secondaryTypographyProps={{ variant: 'caption', fontSize: 10 }}
                    sx={{ minWidth: 0 }}
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Remove">
                      <IconButton edge="end" size="small" onClick={() => onRemoveFile(sig)}>
                        <DeleteOutline sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
                {(index < files.length - 1 || connectorSources.length > 0) && (
                  <Divider component="li" />
                )}
              </React.Fragment>
            );
          })}

          {connectorSources.map((entry, index) => (
            <React.Fragment key={entry.key}>
              <ListItem sx={{ pr: 6 }}>
                <ListItemAvatar sx={{ minWidth: 36 }}>
                  <Avatar sx={{ bgcolor: 'secondary.light', color: 'secondary.dark', width: 24, height: 24 }}>
                    <Cable sx={{ fontSize: 14 }} />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Typography variant="body2" fontSize={12} noWrap>
                        {entry.connectorName}
                      </Typography>
                      <Typography variant="body2" fontSize={12} color="text.secondary">
                        &rarr;
                      </Typography>
                      <Typography variant="body2" fontSize={12} fontFamily="monospace" noWrap>
                        {entry.tableName}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    entry.selectedColumns
                      ? `${entry.selectedColumns.length} column${entry.selectedColumns.length !== 1 ? 's' : ''}`
                      : 'all columns'
                  }
                  secondaryTypographyProps={{ variant: 'caption', fontSize: 10 }}
                  sx={{ minWidth: 0 }}
                />
                <ListItemSecondaryAction>
                  <Tooltip title="Remove">
                    <IconButton edge="end" size="small" onClick={() => onRemoveConnectorSource(entry.key)}>
                      <DeleteOutline sx={{ fontSize: 16 }} />
                    </IconButton>
                  </Tooltip>
                </ListItemSecondaryAction>
              </ListItem>
              {index < connectorSources.length - 1 && <Divider component="li" />}
            </React.Fragment>
          ))}
        </List>
      </Paper>
    </Box>
  );
};

export default SourceBasket;
